import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  programBasicSchema,
  ProgramBasicFormData,
} from "../../schemas/program.schema";
import {
  startUpload,
  uploadSuccess,
  uploadFailure,
} from "../../store/imageSlice";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";
import moment from "moment";
import { fetchUserData } from "../../store/UserSlice";
import { RootState } from "../../store/store";
import { IHealthCondition } from "../../interfaces/HealthCondition";
import { useTranslation } from "react-i18next";
import ProgramField from "./ProgramField";
import ProgramTextarea from "./ProgramTextarea";
import ProgramImageUpload from "./ProgramImageUpload";

interface ProgramBasicsEditProps {
  chnageTab: (tabIndex: number) => void;
  updateHasCounselling?: (hasCounselling: boolean) => void;
}

const ProgramBasicsEdit: React.FC<ProgramBasicsEditProps> = ({
  chnageTab,
  updateHasCounselling,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();

  const [images, setImages] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<ProgramBasicFormData>({
    resolver: zodResolver(programBasicSchema),
    defaultValues: {
      title: "",
      healthCondition: "",
      date: null,
      cost: "",
      participants: "",
      hasCounselling: true,
      totalCounsellings: "",
      zoomLink: "",
      counsellingLink: "",
      description: "",
    },
    mode: "onTouched",
  });

  const hasCounselling = watch("hasCounselling");

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate maximum file size (1MB)
    if (file.size > 1024 * 1024) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      const sizeError = t(
        "adminPrograms.basicForm.imageTooLarge",
        "The image file is too large (max 1MB). Please choose a smaller image."
      );
      setImageError(sizeError);
      toast.error(sizeError);
      return;
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/svg+xml",
      "image/jpg",
    ];
    if (!allowedTypes.includes(file.type)) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      const typeError = t(
        "adminPrograms.basicForm.unsupportedFormat",
        "Unsupported format. Please select a PNG, JPG, or SVG file."
      );
      setImageError(typeError);
      toast.error(typeError);
      return;
    }

    setSelectedImage(file);
    setImageError(null);
  };

  const onSubmit = async (data: ProgramBasicFormData) => {
    if (!selectedImage && !images) {
      const errMsg = t(
        "adminPrograms.basicForm.uploadIconRequired",
        "Please upload an icon for the program"
      );
      setImageError(errMsg);
      toast.warning(errMsg);
      containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    dispatch(startUpload());

    try {
      const formData = new FormData();
      const isValidDate =
        data.date instanceof Date && !isNaN(data.date.getTime());
      formData.append(
        "startDate",
        isValidDate
          ? data.date.toISOString()
          : data.date
          ? new Date(data.date).toISOString()
          : ""
      );
      formData.append("maxParticipants", data.participants);
      formData.append("totalCounsellings", data.totalCounsellings || "");
      formData.append("zoomLink", data.zoomLink);
      formData.append("description", data.description);
      formData.append("startTime", moment().toString());
      formData.append("endTime", moment().toString());
      formData.append("title", data.title);
      formData.append("cost", data.cost);
      formData.append("healthCondition", data.healthCondition);
      formData.append("counsellingLink", data.counsellingLink || "");
      formData.append("hasCounselling", String(data.hasCounselling));
      if (selectedImage) {
        formData.append("icon", selectedImage);
      }
      const response = await apiClient.put(`/api/v1/programs/${id}`, formData);

      dispatch(uploadSuccess(response.data.imageUrl));
      if (response.data.status) {
        toast.info(
          t("adminPrograms.basicForm.updatedSuccess", "Successfully Updated")
        );
        chnageTab(1);
      }
    } catch (error: any) {
      const msg =
        error.response?.status === 413
          ? t(
              "adminPrograms.basicForm.imageTooLarge",
              "The image file is too large (max 1MB). Please upload a smaller image."
            )
          : error.response?.data?.error?.message ||
            error.response?.data?.message ||
            error.message ||
            t("common.error", "An error occurred");
      toast.error(msg);
      dispatch(uploadFailure(msg));
    }
  };

  const updateHasCounsellingRef = useRef(updateHasCounselling);
  useEffect(() => {
    updateHasCounsellingRef.current = updateHasCounselling;
  });

  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        if (!id) return;
        const response = await apiClient.get(`/api/v1/programs/${id}`);
        const p = response?.data?.data;
        if (!p) return;

        const startDateString = p.startDate || "";
        const initialDate = startDateString ? new Date(startDateString) : null;

        setImages(p.icon || null);
        localStorage.setItem("startDate", p.startDate || "");

        const healthConditionValue =
          typeof p.healthCondition === "object" && p.healthCondition?._id
            ? String(p.healthCondition._id)
            : p.healthCondition
            ? String(p.healthCondition)
            : "";

        const isCounselling =
          p.hasCounselling !== false &&
          p.hasCounselling !== "false" &&
          p.hasCounselling !== 0 &&
          p.hasCounselling !== "0";

        reset({
          title: p.title || "",
          healthCondition: healthConditionValue,
          date: initialDate,
          cost: p.cost ? String(p.cost) : "",
          participants: p.maxParticipants ? String(p.maxParticipants) : "",
          hasCounselling: isCounselling,
          totalCounsellings: p.totalCounsellings
            ? String(p.totalCounsellings)
            : "",
          zoomLink: p.zoomLink || "",
          counsellingLink: p.counsellingLink || "",
          description: p.description || "",
        });

        if (updateHasCounsellingRef.current) {
          updateHasCounsellingRef.current(isCounselling);
        }
      } catch (error) {
        console.error("Error fetching health condition details:", error);
      }
    };

    if (id) {
      fetchProgramDetails();
    }
  }, [id, reset]);

  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  const parsedData: any = useSelector((state: RootState) => state.user.data);

  const healthConditionsList: IHealthCondition[] = Array.isArray(parsedData)
    ? parsedData
    : parsedData?.healthConditions || [];

  return (
    <div
      ref={containerRef}
      className="px-8 overflow-y-scroll max-h-[calc(100vh-220px)] scrollbar-hide pb-20"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Image Upload Box */}
        <ProgramImageUpload
          selectedImage={selectedImage}
          images={images}
          imageError={imageError}
          fileInputRef={fileInputRef}
          onImageChange={handleImageChange}
        />

        {/* Title & Health Condition */}
        <div className="flex flex-col md:flex-row">
          <ProgramField
            id="title"
            label={t("adminPrograms.basicForm.title", "Title")}
            placeholder={t(
              "adminPrograms.basicForm.titlePlaceholder",
              "Please select your title"
            )}
            error={errors.title?.message}
            {...register("title")}
          />

          <div className="mb-4 w-full md:w-1/2 pr-4">
            <label
              className="block text-[#607D8B] text-sm mb-2"
              htmlFor="healthCondition"
            >
              {t("adminPrograms.basicForm.healthCondition", "Health Condition")}
            </label>
            <select
              id="healthCondition"
              {...register("healthCondition")}
              className={`border-2 h-11 font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium rounded-[8px] px-3 py-2 w-full transition-colors ${
                errors.healthCondition
                  ? "border-red-400 bg-red-50/40 text-red-600"
                  : !watch("healthCondition")
                  ? "text-[#8D98AA] bg-[#EDF2F6] border-[#E0E4EC]"
                  : "text-[#263238] bg-[#EDF2F6] border-[#E0E4EC]"
              }`}
            >
              <option value="" disabled>
                {t(
                  "adminPrograms.basicForm.selectHealthCondition",
                  "-- Select Health Condition --"
                )}
              </option>
              {healthConditionsList.map((condition: IHealthCondition) => (
                <option
                  key={condition.id || (condition as any)._id}
                  value={condition.id || (condition as any)._id}
                  className="text-[#263238]"
                >
                  {condition.name}
                </option>
              ))}
            </select>
            {errors.healthCondition && (
              <p className="text-xs text-red-500 mt-1 font-gilroy-medium">
                {errors.healthCondition.message}
              </p>
            )}
          </div>
        </div>

        {/* Date, Cost, Max Participants */}
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full md:w-1/2 pr-4">
            <label className="block text-[#607D8B] text-sm mb-2" htmlFor="date">
              {t("adminPrograms.basicForm.date", "Date")}
            </label>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  placeholder={t(
                    "adminPrograms.basicForm.datePlaceholder",
                    "MM/DD/YYYY"
                  )}
                  inputClass={`border-2 text-[#263238] bg-[#EDF2F6] placeholder:text-[#8D98AA] font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium rounded-[8px] px-3 py-2 focus:outline-none w-full transition-colors ${
                    errors.date ? "border-red-400 bg-red-50/40" : "border-[#E0E4EC]"
                  }`}
                  containerClassName="w-full"
                  calendarPosition="bottom"
                  id="date"
                  highlightToday={true}
                  onChange={(dateVal) => {
                    const parsed = dateVal
                      ? moment(dateVal.toString()).toDate()
                      : null;
                    field.onChange(parsed);
                  }}
                  format="MM/DD/YYYY"
                  value={
                    field.value
                      ? moment(field.value).format("MM/DD/YYYY")
                      : ""
                  }
                  minDate={new DateObject()}
                />
              )}
            />
            {errors.date && (
              <p className="text-xs text-red-500 mt-1 font-gilroy-medium">
                {errors.date.message as string}
              </p>
            )}
          </div>

          <ProgramField
            id="cost"
            label={t("adminPrograms.basicForm.cost", "Cost")}
            placeholder={t(
              "adminPrograms.basicForm.costPlaceholder",
              "Enter a Cost"
            )}
            error={errors.cost?.message}
            {...register("cost")}
          />

          <ProgramField
            id="participants"
            label={t("adminPrograms.basicForm.maxParticipants", "Max participants")}
            placeholder={t(
              "adminPrograms.basicForm.participantsPlaceholder",
              "Enter a participants"
            )}
            error={errors.participants?.message}
            {...register("participants")}
          />
        </div>

        {/* 1-1 Counselling Checkbox */}
        <div className="flex flex-col md:flex-row mb-4">
          <div className="flex items-center w-full md:w-1/2 pr-4">
            <Controller
              control={control}
              name="hasCounselling"
              render={({ field: { value, onChange, ref } }) => (
                <input
                  type="checkbox"
                  id="hasCounselling"
                  ref={ref}
                  checked={Boolean(value)}
                  onChange={async (e) => {
                    const isChecked = e.target.checked;
                    onChange(isChecked);
                    if (updateHasCounselling) {
                      updateHasCounselling(isChecked);
                    }
                    if (id) {
                      try {
                        const formData = new FormData();
                        formData.append("hasCounselling", String(isChecked));
                        await apiClient.put(`/api/v1/programs/${id}`, formData);
                      } catch (err) {
                        console.error("Auto-sync hasCounselling failed:", err);
                      }
                    }
                  }}
                  className="mr-2 h-4 w-4 cursor-pointer"
                />
              )}
            />
            <label className="text-[#607D8B] text-sm cursor-pointer select-none" htmlFor="hasCounselling">
              {t("adminPrograms.basicForm.hasCounselling", "Has 1-1 Counselling")}
            </label>
          </div>
        </div>

        {/* Total Counsellings Slots (shown if hasCounselling) */}
        {hasCounselling && (
          <div className="flex flex-col md:flex-row">
            <ProgramField
              id="totalCounsellings"
              label={t(
                "adminPrograms.basicForm.totalCounsellings",
                "Total Counsellings"
              )}
              placeholder={t(
                "adminPrograms.basicForm.counsellingSlotsPlaceholder",
                "Enter a total Counselling slots"
              )}
              error={errors.totalCounsellings?.message}
              {...register("totalCounsellings")}
            />
          </div>
        )}

        {/* Zoom Link, Counselling Link, Description */}
        <div className="flex flex-col">
          <ProgramField
            id="zoomLink"
            fullWidth
            label={t("adminPrograms.basicForm.zoomLink", "Zoom link")}
            placeholder={t(
              "adminPrograms.basicForm.zoomLinkPlaceholder",
              "Enter a zoom link"
            )}
            error={errors.zoomLink?.message}
            {...register("zoomLink")}
          />

          {hasCounselling && (
            <ProgramField
              id="counsellingLink"
              fullWidth
              label={t(
                "adminPrograms.basicForm.counsellingLink",
                "Counselling 1-1 Meeting link"
              )}
              placeholder={t(
                "adminPrograms.basicForm.counsellingLinkPlaceholder",
                "Enter a Counselling 1-1 Meeting link"
              )}
              error={errors.counsellingLink?.message}
              {...register("counsellingLink")}
            />
          )}

          <ProgramTextarea
            id="description"
            fullWidth
            label={t("adminPrograms.basicForm.description", "Description")}
            placeholder={t(
              "adminPrograms.basicForm.descriptionPlaceholder",
              "Enter a description"
            )}
            error={errors.description?.message}
            {...register("description")}
          />
        </div>

        {/* Action Buttons */}
        <button
          type="submit"
          className="bg-[#B4804A] hover:bg-[#a06f3c] active:scale-98 text-white font-semibold float-right w-32 py-2 px-4 mb-8 rounded-md transition-all shadow-xs"
        >
          {t("adminPrograms.basicForm.save", "Save")}
        </button>
        <Link
          to="/admin/programs"
          className="bg-[#90A4AE] hover:bg-[#7d939e] active:scale-98 text-white float-right font-semibold w-32 py-2 px-4 mb-8 mr-6 text-center rounded-md transition-all shadow-xs"
        >
          {t("adminPrograms.basicForm.cancel", "Cancel")}
        </Link>
      </form>
    </div>
  );
};

export default ProgramBasicsEdit;
